import React, { useState } from 'react';
import { useStore } from '../state/store';
import { Card, Chip, Banner, Area, Select, Check, SectionHead, ChecklistRow, Stat } from '../components/ui';
import { weightOf } from '../lib/derive';
import { deriveGateReadiness } from '../lib/gates';
import { orgPrimaryDeal, orgPrimaryDealEnabled } from '../lib/orgPlan';
import { GateAccountabilityBand } from '../components/GateAccountabilityBand';
import { SprintPostMortemForm } from '../components/Forms';
import { findPostMortemForSprint, isPostMortemComplete } from '../lib/sprintPostMortem';
import { sprintStartOf } from '../lib/sprint';
import { fmtDate } from '../lib/dates';

export default function Gates() {
  const { state, d } = useStore();
  const gates = d.sprint.gates || [];
  const [tab, setTab] = useState(() => {
    const next = d.sprint.nextGate;
    return next ? next.key : 'g45';
  });

  return (
    <>
      <div className="page-head">
        <div className="row">
          <h2>Decision gates</h2>
          <div className="spacer" />
          <div className="seg">
            {gates.map((g) => (
              <button key={g.key} className={tab === g.key ? 'on' : ''} onClick={() => setTab(g.key)}>
                {g.label}
              </button>
            ))}
          </div>
        </div>
        <div className="sub">
          Evidence created after a gate does not count for that gate.
          {d.sprint.phase === 'pre'
            ? ` PRE · T−${d.sprint.daysUntilStart ?? '?'} · execution begins ${d.sprint.start || 'T0'}`
            : ` Day ${d.sprint.day} of 45`}
          {d.sprint.phase !== 'pre' && d.sprint.start ? ` · started ${d.sprint.start}` : ''}.
        </div>
      </div>

      <GateAccountabilityBand accountability={d.gateAccountability} go={undefined} compact />

      {tab === 'g15' && <Day15 />}
      {tab === 'g30' && <Day30 />}
      {tab === 'g45' && <Day45 />}
    </>
  );
}

function GateReadinessHeader({ day }) {
  const { state, d } = useStore();
  const r = deriveGateReadiness(state, d, day);
  return (
    <section className="section">
      <Card title={r.label} sub="From recorded state. Founder checkboxes below do not rewrite this.">
        <div className="grid g3">
          <div>
            <div className="dim tiny">Likely Pass</div>
            {(r.likelyPass || []).length === 0 ? <div className="muted">None yet</div> : r.likelyPass.map((it) => (
              <div key={it.label} style={{ marginTop: 6 }}><Chip tone="green">Pass</Chip> {it.label}</div>
            ))}
          </div>
          <div>
            <div className="dim tiny">At Risk</div>
            {(r.atRisk || []).length === 0 ? <div className="muted">None</div> : r.atRisk.map((it) => (
              <div key={it.label} style={{ marginTop: 6 }}><Chip tone="amber">At risk</Chip> {it.label}</div>
            ))}
          </div>
          <div>
            <div className="dim tiny">Missing</div>
            {(r.missing || []).length === 0 ? <div className="muted">None</div> : r.missing.map((it) => (
              <div key={it.label} style={{ marginTop: 6 }}><Chip tone="red">Missing</Chip> {it.label}</div>
            ))}
          </div>
        </div>
        {(r.smallestActions || []).length > 0 && (
          <p className="muted" style={{ marginTop: 12 }}>
            Smallest close: {r.smallestActions.join(' ')}
          </p>
        )}
      </Card>
    </section>
  );
}

function ActionReview({ gateDay, label }) {
  const { d } = useStore();
  const review = d.top3?.gateReview?.[gateDay] || { required: [], completed: [], overdue: [], blocked: [], evidenceCreated: [], missing: [] };
  return (
    <section className="section">
      <SectionHead
        title="Action review"
        hint={`What must be done for ${label}. Actions persist; this is the gate cut of the backlog.`}
      />
      <div className="grid g4">
        <Card><Stat label="Required" value={<span className="num">{review.required.length}</span>} size="sm" foot="Marked for this gate" /></Card>
        <Card><Stat label="Completed" value={<span className="num">{review.completed.length}</span>} size="sm" /></Card>
        <Card><Stat label="Overdue" value={<span className="num">{review.overdue.length}</span>} size="sm" tone={review.overdue.length ? 'red' : undefined} /></Card>
        <Card><Stat label="Blocked" value={<span className="num">{review.blocked.length}</span>} size="sm" /></Card>
      </div>
      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="Evidence created from these actions">
          {review.evidenceCreated.length === 0 ? (
            <div className="dim tiny">No E3+ evidence recorded against gate actions yet.</div>
          ) : (
            review.evidenceCreated.map((a) => (
              <div key={a.id} style={{ marginBottom: 6 }}>
                {a.text} <span className="dim tiny">→ {a.evidenceCreated}</span>
              </div>
            ))
          )}
        </Card>
        <Card title="Critical missing">
          {review.missing.length === 0 ? (
            <div className="dim tiny">No Critical/High open actions marked for this gate.</div>
          ) : (
            review.missing.map((a) => (
              <div key={a.id} style={{ marginBottom: 6 }}>
                <strong>{a.priority}.</strong> {a.text}
                {a.dueDate ? <span className="dim tiny"> · due {a.dueDate}</span> : null}
              </div>
            ))
          )}
        </Card>
      </div>
    </section>
  );
}

function useGate(key) {
  const { state, set } = useStore();
  const data = state.gateReviews?.[key] || {};
  const upd = (k) => (v) => set(`gateReviews.${key}.${k}`, v);
  return [data, upd];
}

/* -------------------------------------------------------------- day 15 -- */

function Day15() {
  const { state, d } = useStore();
  const [g, upd] = useGate('g15');
  const { funnel } = d;
  const c = funnel.counts;

  const r = deriveGateReadiness(state, d, 15);
  const observed = r.checks || [];

  const bottleneck = funnel.diagnosis[0]?.stage
    || ((c.contacted || 0) < 3 ? 'Constraint not yet assessable'
      : (c.engaged || 0) < 5 ? 'Access / outreach conversion'
        : 'Not clear from the data');

  return (
    <>
      <GateReadinessHeader day={15} />
      <ActionReview gateDay={15} label="Day 15" />
      <Banner>
        {orgPrimaryDealEnabled(state) ? (
          <>
            Day-15 objective: <strong>enough market learning and opportunity density to identify the emerging winners.</strong>{' '}
            A {orgPrimaryDeal(state)?.label || 'primary deal'} is <strong>not</strong> required at Day 15. This gate also does not offer "change country" —
            changing market is a Strategy Exception, not a menu option.
          </>
        ) : (
          <>
            Day-15 objective: <strong>Week-1 priorities live, ≥10 touches, ≥1 discovery on the package wedge.</strong>{' '}
            A SOW is <strong>not</strong> required at Day 15. Changing plan DNA is a Strategy Exception / recompile —
            not a menu option.
          </>
        )}
      </Banner>

      <section className="section grid g-2-1">
        <Card title="What the recorded state says">
          {observed.map((o, i) => (
            <ChecklistRow key={i} ok={o.ok} label={o.label} detail={o.detail} />
          ))}
          <div className="hr" />
          <div className="row">
            <span className="dim tiny">Is the bottleneck top-of-funnel or conversion?</span>
            <div className="spacer" />
            <Chip tone={/outreach|access/i.test(bottleneck) ? 'amber' : ''}>{bottleneck}</Chip>
          </div>
          <div className="dim tiny" style={{ marginTop: 6 }}>
            {funnel.diagnosis[0] ? funnel.diagnosis[0].text : 'Funnel counts are too small to diagnose a leak yet.'}
          </div>
        </Card>
        <Card title="Action">
          <Select
            label="Decision"
            value={g.action || ''}
            onChange={upd('action')}
            options={['', 'Continue', 'Adjust ICP', 'Adjust pitch', 'Escalate regulatory architecture']}
          />
          <Area label="Reasoning" value={g.reasoning || ''} onChange={upd('reasoning')} rows={5} placeholder="What did the first third of the sprint prove?" />
          {g.action && <Banner tone="ok">Recorded. Copy this into the Decision Log if it changes how the next two weeks run.</Banner>}
        </Card>
      </section>
    </>
  );
}

/* -------------------------------------------------------------- day 30 -- */

function Day30() {
  const { state, d } = useStore();
  const [g, upd] = useGate('g30');
  const { d001, saudi, infra, evidence, funnel } = d;
  const c = funnel.counts;
  const t = funnel.targets || {};

  const densityTests = [
    { ok: c.qualified >= (t.qualifiedOpportunities || 4), label: `4–6 qualified opportunities`, detail: `${c.qualified} recorded` },
    { ok: c.assetsIdentified >= (t.assets || 2), label: '2–3 credible assets', detail: `${c.assetsIdentified} recorded` },
    { ok: c.candidates >= (t.candidates || 2), label: 'Ideally 2 D001 candidates', detail: `${c.candidates} recorded` },
    { ok: c.primary >= 1, label: 'One Primary D001 if evidence supports selection', detail: funnel.primary ? funnel.primary.name : 'none designated' },
    { ok: c.backups >= 1, label: 'At least one backup path', detail: `${c.backups} backup(s)` },
  ];
  const densityMet = densityTests.filter((x) => x.ok).length;

  const dubaiTests = [
    { ok: !!state.d001?.asset?.namedAssetExists && !!state.d001?.asset?.titleInfoAvailable, label: 'Real asset + documents' },
    { ok: (state.infra?.opportunities || []).some((o) => o.paidPoc) || !!g.paidProposal, label: 'Paid readiness / implementation proposal' },
    { ok: (evidence.byMarket.Dubai?.E4 || 0) + (evidence.byMarket.Dubai?.E5 || 0) > 0, label: 'Sponsor at E4 or better' },
    { ok: d001.partnerReady.length > 0, label: 'Partner at Pilot Ready' },
    { ok: (state.d001?.capital?.conversations || []).some((c) => c.acceptanceSignal), label: 'D001 capital signal' },
  ];
  const dubaiPass = dubaiTests.filter((t) => t.ok).length >= 2;

  const saudiTests = [
    { ok: saudi.totals.budget > 0, label: 'Budget identified' },
    { ok: saudi.totals.proposals > 0 || saudi.totals.paid > 0, label: 'POC or proposal' },
    { ok: saudi.signals.find((s) => s.key === 'useCase')?.value === 'strong', label: 'Real contribution / use case' },
    { ok: saudi.signals.find((s) => s.key === 'regulatedPartner')?.value !== 'none', label: 'Regulated institution wants integration' },
    { ok: saudi.signals.find((s) => s.key === 'government')?.value === 'strong', label: 'REGA concrete next gate' },
  ];
  const saudiPass = saudiTests.filter((t) => t.ok).length >= 2;

  const infraTests = [
    { ok: infra.live.some((o) => Number(String(o.proposalValue).replace(/[^0-9.]/g, '')) > 0 && weightOf(o.evidenceLevel) >= 4), label: 'Serious priced proposal' },
    { ok: !!g.buildVsBuy, label: 'Buyer confirms the build-vs-buy problem' },
  ];
  const infraPass = infraTests.every((t) => t.ok);

  const shift = recommendShift({ dubaiPass, saudiPass, infraPass, saudiSignals: saudi.count });

  return (
    <>
      <GateReadinessHeader day={30} />
      <ActionReview gateDay={30} label="Day 30" />
      <section className="section">
        <Card
          title="Opportunity density"
          sub="Operating hypotheses, not automatic failure thresholds"
          right={<Chip tone={densityMet >= 3 ? 'green' : densityMet >= 2 ? 'amber' : 'red'}>{densityMet} / 5 met</Chip>}
        >
          <div className="grid g2">
            <div>
              {densityTests.map((x, i) => (
                <ChecklistRow key={i} ok={x.ok} label={x.label} detail={x.detail} />
              ))}
            </div>
            <div>
              <Banner tone={densityMet >= 3 ? 'ok' : 'warn'}>
                <strong>Do we have enough opportunity density to justify concentrating on one Primary D001?</strong>
                <div style={{ marginTop: 6 }}>
                  {densityMet >= 3
                    ? 'Yes on the recorded data — concentrate, and keep one backup alive.'
                    : 'Not yet. Diagnose the funnel stage before increasing product or regulatory work.'}
                </div>
              </Banner>
              <div style={{ marginTop: 10 }}>
                <Select
                  label="Founder call"
                  value={g.density || ''}
                  onChange={upd('density')}
                  options={['', 'Concentrate on Primary D001', 'Keep building density', 'Diagnose the funnel first']}
                />
              </div>
              {funnel.diagnosis[0] && <div className="dim tiny">Largest apparent leak: {funnel.diagnosis[0].stage} — {funnel.diagnosis[0].text}</div>}
            </div>
          </div>
        </Card>
      </section>

      <section className="section grid g3">
        <Card title="Dubai" right={<Chip tone={dubaiPass ? 'green' : 'red'}>{dubaiPass ? 'Passes' : 'Does not pass'}</Chip>}>
          <div className="dim tiny" style={{ marginBottom: 6 }}>Passes if at least 2 are true.</div>
          {dubaiTests.map((t, i) => <ChecklistRow key={i} ok={t.ok} label={t.label} />)}
          <Check label="Paid readiness / implementation proposal issued" checked={!!g.paidProposal} onChange={upd('paidProposal')} />
        </Card>

        <Card title="Saudi" right={<Chip tone={saudiPass ? 'green' : 'red'}>{saudiPass ? 'Earns more attention' : 'Stays parked'}</Chip>}>
          <div className="dim tiny" style={{ marginBottom: 6 }}>Earns more attention if at least 2 are true.</div>
          {saudiTests.map((t, i) => <ChecklistRow key={i} ok={t.ok} label={t.label} />)}
        </Card>

        <Card title="Infrastructure" right={<Chip tone={infraPass ? 'green' : 'red'}>{infraPass ? 'Passes' : 'Does not pass'}</Chip>}>
          <div className="dim tiny" style={{ marginBottom: 6 }}>Passes only if both are true.</div>
          {infraTests.map((t, i) => <ChecklistRow key={i} ok={t.ok} label={t.label} />)}
          <Check label="A buyer has confirmed the build-vs-buy problem" checked={!!g.buildVsBuy} onChange={upd('buildVsBuy')} />
        </Card>
      </section>

      <section className="section grid g-2-1">
        <Card title="Recommended resource shift" sub="Derived from evidence, not preference">
          <div className="grid g5">
            {shift.map((s) => (
              <Stat key={s.label} label={s.label} value={<span className="num">{s.pct}%</span>} foot={s.delta === 0 ? 'unchanged' : `${s.delta > 0 ? '+' : ''}${s.delta} pts`} />
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <Banner tone={shift.some((s) => s.delta !== 0) ? 'warn' : 'ok'}>
              {shift.some((s) => s.delta !== 0)
                ? 'A shift is indicated. Adopting it still requires a logged decision — the allocation is part of the locked strategy.'
                : 'Evidence does not justify moving founder time. Hold the locked allocation.'}
            </Banner>
          </div>
        </Card>
        <Card title="Decision">
          <Area label="What changes for the final 15 days?" value={g.decision || ''} onChange={upd('decision')} rows={7} />
        </Card>
      </section>
    </>
  );
}

function recommendShift({ dubaiPass, saudiPass, infraPass, saudiSignals }) {
  const base = { Dubai: 55, Saudi: 20, Infrastructure: 10, Fundraising: 10, Other: 5 };
  const next = { ...base };
  if (dubaiPass) next.Dubai += 5;
  if (!dubaiPass) next.Dubai -= 5;
  if (saudiPass && saudiSignals >= 2) next.Saudi += 5;
  else if (!saudiPass) next.Saudi -= 5;
  if (infraPass) next.Infrastructure += 10;
  const total = Object.values(next).reduce((a, b) => a + b, 0);
  const scale = 100 / total;
  return Object.keys(base).map((k) => {
    const pct = Math.round(next[k] * scale);
    return { label: k, pct, delta: pct - base[k] };
  });
}

/* -------------------------------------------------------------- day 45 -- */

const OPTIONS = [
  { key: 'A', label: 'A — Dubai proof + Saudi pull', desc: 'Both markets produced evidence. Run them together with Dubai leading.' },
  { key: 'B', label: 'B — Dubai proof / Saudi weak', desc: 'Concentrate on the transaction market. Park Saudi.' },
  { key: 'C', label: 'C — Saudi proof / Dubai weak', desc: 'The scale market moved and the transaction market did not. Restructure around it.' },
  { key: 'D', label: 'D — Infrastructure proof / marketplace slow', desc: 'The cash engine is the company for now.' },
  { key: 'E', label: 'E — No meaningful proof', desc: 'Nothing reached E4. The honest options are narrow and this screen will not soften them.' },
];

function Day45() {
  const { state, d } = useStore();
  const [g, upd] = useGate('g45');
  const [pmOpen, setPmOpen] = useState(false);
  const { evidence, saudi, infra, funnel } = d;
  const c = funnel.counts;
  const existingPm = findPostMortemForSprint(state, sprintStartOf(state));
  const pmDone = isPostMortemComplete(existingPm);

  // Two independent questions: did the deal prove, and did the machine prove?
  const dealTests = [
    { ok: (evidence.byMarket.Dubai?.E4 || 0) + (evidence.byMarket.Dubai?.E5 || 0) > 0, label: 'Primary D001 reached E4 or E5' },
    { ok: !!g.paidReadiness, label: 'Paid readiness, signed mandate or implementation agreement' },
    { ok: !!g.bindingPath, label: 'A binding transaction path exists' },
  ];
  const dealStrong = dealTests.filter((x) => x.ok).length >= 1;

  const funnelTests = [
    { ok: c.universe >= 40, label: 'Can generate relevant targets at volume', detail: `${c.universe} accounts` },
    { ok: c.discovery >= 8, label: 'Can reach qualified sponsor conversations', detail: `${c.discovery} discoveries` },
    { ok: c.qualified >= 3, label: 'Can convert conversations into real opportunities', detail: `${c.qualified} qualified` },
    { ok: c.assetsIdentified >= 2, label: 'Can surface credible assets', detail: `${c.assetsIdentified} assets` },
  ];
  const funnelStrong = funnelTests.filter((x) => x.ok).length >= 3;

  const matrix = dealStrong && funnelStrong
    ? { label: 'Strong deal + strong funnel', text: 'Excellent. The transaction and the machine that produced it both worked.' }
    : dealStrong
      ? { label: 'Strong deal + weak funnel', text: 'The deal may be founder luck rather than a repeatable motion. Treat repeatability as the next quarter\'s question.' }
      : funnelStrong
        ? { label: 'Weak deal + strong funnel', text: 'Origination works; closing or execution does not. This is a commercial conversion problem, not a market-access problem.' }
        : { label: 'Weak deal + weak funnel', text: 'ICP, proposition or market access is wrong. More activity in the same direction will not fix it.' };

  const dubaiProof = (evidence.byMarket.Dubai?.E4 || 0) + (evidence.byMarket.Dubai?.E5 || 0) > 0;
  const saudiProof = saudi.count >= 2;
  const infraProof = (evidence.byMarket.Infrastructure?.E4 || 0) + (evidence.byMarket.Infrastructure?.E5 || 0) > 0;
  const suggested = !dubaiProof && !saudiProof && !infraProof
    ? 'E'
    : dubaiProof && saudiProof
      ? 'A'
      : dubaiProof
        ? 'B'
        : saudiProof
          ? 'C'
          : 'D';

  return (
    <>
      <GateReadinessHeader day={45} />
      <ActionReview gateDay={45} label="Day 45" />
      <section className="grid g4">
        <Card><Stat label="Dubai E4+E5" term="e4E5Commitments" value={<span className="num">{(evidence.byMarket.Dubai?.E4 || 0) + (evidence.byMarket.Dubai?.E5 || 0)}</span>} /></Card>
        <Card><Stat label="Saudi strong signals" value={<span className="num">{saudi.count} / 4</span>} /></Card>
        <Card><Stat label="Infrastructure E4+E5" term="e4E5Commitments" value={<span className="num">{(evidence.byMarket.Infrastructure?.E4 || 0) + (evidence.byMarket.Infrastructure?.E5 || 0)}</span>} /></Card>
        <Card><Stat label="Paid POCs" value={<span className="num">{infra.paidPocs}</span>} /></Card>
      </section>

      <section className="section grid g3">
        <Card title="Deal proof" right={<Chip tone={dealStrong ? 'green' : 'red'}>{dealStrong ? 'Strong' : 'Weak'}</Chip>}>
          {dealTests.map((x, i) => <ChecklistRow key={i} ok={x.ok} label={x.label} />)}
          <div className="hr" />
          <Check label="Paid readiness / signed mandate / implementation" checked={!!g.paidReadiness} onChange={upd('paidReadiness')} />
          <Check label="Binding transaction path exists" checked={!!g.bindingPath} onChange={upd('bindingPath')} />
        </Card>

        <Card title="Funnel proof" right={<Chip tone={funnelStrong ? 'green' : 'red'}>{funnelStrong ? 'Strong' : 'Weak'}</Chip>}>
          {funnelTests.map((x, i) => <ChecklistRow key={i} ok={x.ok} label={x.label} detail={x.detail} />)}
          <div className="dim tiny" style={{ marginTop: 6 }}>
            Did the company demonstrate it can repeatedly generate relevant targets, qualified conversations, real
            opportunities and credible assets?
          </div>
        </Card>

        <Card title="Diagnostic" right={<Chip tone={dealStrong && funnelStrong ? 'green' : dealStrong || funnelStrong ? 'amber' : 'red'}>{matrix.label}</Chip>}>
          <div style={{ fontSize: 13.5 }}>{matrix.text}</div>
        </Card>
      </section>

      <section className="section">
        <SectionHead title="Operating model" hint={`Evidence points to option ${suggested}. You must still choose.`} />
        <div className="grid g5">
          {OPTIONS.map((o) => (
            <button
              key={o.key}
              className="card"
              style={{
                textAlign: 'left', cursor: 'pointer', padding: 0,
                borderColor: g.option === o.key ? 'var(--ink)' : undefined,
                borderWidth: g.option === o.key ? 2 : 1,
              }}
              onClick={() => upd('option')(o.key)}
            >
              <div className="card-body">
                <div className="row tight">
                  <strong>{o.label}</strong>
                  {suggested === o.key && <Chip tone="accent">indicated</Chip>}
                </div>
                <div className="dim tiny" style={{ marginTop: 6 }}>{o.desc}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="section grid g2">
        <Card title="What did the market prove?">
          <Area label="" value={g.proved || ''} onChange={upd('proved')} rows={5} placeholder="Only things a counterparty did." />
        </Card>
        <Card title="What did the market disprove?">
          <Area label="" value={g.disproved || ''} onChange={upd('disproved')} rows={5} />
        </Card>
        <Card title="What should we stop?">
          <Area label="" value={g.stop || ''} onChange={upd('stop')} rows={5} />
        </Card>
        <Card title="Where do we concentrate next quarter?">
          <Area label="" value={g.concentrate || ''} onChange={upd('concentrate')} rows={5} />
        </Card>
      </section>

      {g.option === 'E' && (
        <section className="section">
          <Banner tone="stop">
            No meaningful proof is a result, not a failure of effort. It is the one outcome that most justifies changing
            the shape of the company rather than repeating the sprint.
          </Banner>
        </section>
      )}

      <section className="section">
        <Card
          title="Sprint post-mortem"
          sub="Archive lessons for this T0. Required before re-baselining into the next sprint."
          right={pmDone ? <Chip tone="green">Saved</Chip> : <Chip tone="amber">Required</Chip>}
        >
          {pmDone ? (
            <>
              <p className="bridge-copy">
                Carry-forward: <strong>{existingPm.carryForward}</strong>
              </p>
              <div className="dim tiny" style={{ marginTop: 6 }}>
                Saved {fmtDate(existingPm.endedAt)}
                {existingPm.option ? ` · option ${existingPm.option}` : ''}
                {existingPm.earlyClose ? ' · early close' : ''}
              </div>
              <button type="button" className="btn sm" style={{ marginTop: 10 }} onClick={() => setPmOpen(true)}>
                Edit post-mortem
              </button>
            </>
          ) : (
            <>
              <p className="dim tiny">
                Prefills from the Day-45 fields above. Add the miss, rationale, carry-forward, and honest close.
              </p>
              <button type="button" className="btn primary" style={{ marginTop: 10 }} onClick={() => setPmOpen(true)}>
                Save sprint post-mortem
              </button>
            </>
          )}
        </Card>
      </section>

      {pmOpen && (
        <SprintPostMortemForm
          earlyClose={false}
          endedBy="day45"
          onClose={() => setPmOpen(false)}
        />
      )}
    </>
  );
}
